import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { assetsAPI, taxonomyAPI, locationsAPI, sopsAPI, getMediaURL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, Search, QrCode, Edit2, Trash2, Download, Package,
  MapPin, Tag, RefreshCw, X, ChevronRight, Layers, FileUp, FileSpreadsheet, Clock,
  History as HistoryIcon, MessageSquare, BookOpen, Eye, Copy, LayoutGrid, List,
  Printer, ExternalLink, Shield, CheckCircle, AlertTriangle, Building, Hash, Type
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { format, parseISO } from 'date-fns';

const STATUS_OPTIONS = ['new', 'operational', 'under_maintenance', 'faulty', 'decommissioned'];
const avatarColors = ['#0EA5E9', '#8B5CF6', '#10B981', '#F97316', '#EF4444', '#F59E0B'];
const PRIORITY_COLORS = { critical: '#EF4444', high: '#F97316', medium: '#F59E0B', low: '#10B981' };

function QRModal({ asset, onClose, onRegenerate }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    assetsAPI.getQRImage(asset.id).then(r => setQrData(r.data));
  }, [asset.id]);

  const download = () => {
    const a = document.createElement('a');
    a.href = qrData.qr_image;
    a.download = `QR-${asset.asset_code}.png`;
    a.click();
  };

  const print = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return toast.error('Please allow popups to print');
    printWindow.document.write(`
      <html>
        <head>
          <title>Asset QR - ${asset.asset_code}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; text-align: center; padding: 40px; }
            .label-card { display: inline-block; border: 2px solid #0F172A; border-radius: 12px; padding: 20px 24px; max-width: 320px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
            h2 { margin: 0 0 6px 0; font-size: 20px; color: #0F172A; }
            .code { font-family: monospace; font-size: 15px; font-weight: 700; color: #0EA5E9; margin-bottom: 14px; }
            img { width: 200px; height: 200px; margin-bottom: 12px; }
            .meta { font-size: 12px; color: #64748B; font-weight: 500; }
          </style>
        </head>
        <body>
          <div class="label-card">
            <h2>${asset.name}</h2>
            <div class="code">${asset.asset_code}</div>
            <img src="${qrData.qr_image}" alt="QR" />
            <div class="meta">${asset.location || asset.site_name || 'FieldOps Pro Asset'}</div>
          </div>
          <script>window.onload = () => { window.print(); window.close(); };<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure? This will invalidate the previous QR code and technicians will need to scan the new one.')) return;
    setLoading(true);
    try {
      const res = await assetsAPI.regenerateQR(asset.id);
      setQrData({
        qr_image: res.data.qr_image,
        qr_code: res.data.asset.qr_code,
        name: res.data.asset.name,
        asset_code: res.data.asset.asset_code
      });
      toast.success('QR Code regenerated');
      if (onRegenerate) onRegenerate();
    } catch {
      toast.error('Failed to regenerate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">QR Code Label</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{asset.name}</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          {qrData ? (
            <>
              {asset.qr_regenerated_at && (
                <div style={{ fontSize: 11, background: 'var(--warning-light)', color: 'var(--warning-dark)', padding: '4px 8px', borderRadius: 4, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <Clock size={10} /> Last regenerated: {new Date(asset.qr_regenerated_at).toLocaleString()}
                </div>
              )}
              <div style={{
                background: '#FFFFFF', padding: 20, borderRadius: 'var(--r-lg)',
                display: 'inline-block', border: '1px solid var(--border)', marginBottom: 16,
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
              }}>
                <img src={qrData.qr_image} alt="QR" style={{ width: 200, height: 200 }} />
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                {qrData.qr_code}
              </div>
              <div style={{
                background: 'var(--bg-muted)', borderRadius: 'var(--r-md)', padding: '10px 16px',
                fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16
              }}>
                <strong>{asset.asset_code}</strong> · {asset.name}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={handleRegenerate} disabled={loading} style={{ flex: 1 }}>
                  {loading ? <div className="spinner" /> : <RefreshCw size={14} />} Regenerate
                </button>
                <button className="btn btn-ghost" onClick={print} style={{ flex: 1 }}>
                  <Printer size={14} /> Print
                </button>
                <button className="btn btn-primary" onClick={download} style={{ flex: 1.2 }}>
                  <Download size={14} /> Download
                </button>
              </div>
            </>
          ) : <div style={{ padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}
        </div>
      </div>
    </div>
  );
}

function AssetImportModal({ onClose, onSave }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
    maxFiles: 1,
    onDrop: ([f]) => { setFile(f); setErrors([]); }
  });

  const downloadTemplate = async () => {
    try {
      const res = await assetsAPI.getTemplate();
      let blob = res.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'asset_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setErrors([]);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await assetsAPI.import(fd);
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        setErrors(res.data.errors);
      } else {
        onSave();
      }
    } catch (err) {
      const details = err.response?.data?.details;
      if (Array.isArray(details)) setErrors(details);
      else setErrors([err.response?.data?.error || 'Import failed']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Bulk Import Assets</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Upload Excel file to create multiple assets at once</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: '12px 16px', background: 'var(--bg-muted)', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FileSpreadsheet size={20} color="var(--accent)" />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Download Excel Template</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}><Download size={14} /> Download</button>
          </div>

          <div {...getRootProps()} className={`drop-zone ${isDragActive ? 'active' : ''}`} style={{ padding: '40px 20px', borderStyle: file ? 'solid' : 'dashed' }}>
            <input {...getInputProps()} />
            {file ? (
              <div style={{ textAlign: 'center' }}>
                <FileSpreadsheet size={32} color="var(--accent)" style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 14, fontWeight: 700 }}>{file.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{(file.size / 1024).toFixed(1)} KB · Click or drop to change</div>
              </div>
            ) : (
              <>
                <div className="drop-zone-icon"><FileUp size={32} /></div>
                <div className="drop-zone-title">Drop Excel file here</div>
                <div className="drop-zone-sub">Only .xlsx or .xls files are supported</div>
              </>
            )}
          </div>

          {errors.length > 0 && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: 'var(--r-md)', padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 6 }}>Import Issues ({errors.length}):</div>
              <div style={{ maxHeight: 120, overflow: 'auto', fontSize: 12, color: '#B91C1C', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {errors.map((err, i) => <div key={i}>• {err}</div>)}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={loading || !file} style={{ width: 130, justifyContent: 'center' }}>
            {loading ? <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : 'Import Assets'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetModal({ asset, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: asset?.name || '',
    asset_code: asset?.asset_code || '',
    description: asset?.description || '',
    nature_id: asset?.nature_id || '',
    category_id: asset?.category_id || '',
    type_id: asset?.type_id || '',
    location: asset?.location || '',
    manufacturer: asset?.manufacturer || '',
    model: asset?.model || '',
    serial_number: asset?.serial_number || '',
    installation_date: asset?.installation_date ? asset.installation_date.slice(0, 10) : '',
    status: asset?.status || 'operational',
    criticality: asset?.criticality || 'medium',
    site_id: asset?.site_id || '',
    location_id: asset?.location_id || '',
  });
  const [natures, setNatures] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      taxonomyAPI.natures(),
      locationsAPI.sites()
    ]).then(([nr, sr]) => {
      setNatures(nr.data.natures || []);
      setSites(sr.data.sites || []);

      if (asset?.nature_id) {
        taxonomyAPI.categories({ nature_id: asset.nature_id }).then(cr => setFilteredCategories(cr.data.categories || []));
      }
      if (asset?.category_id) {
        taxonomyAPI.types({ category_id: asset.category_id }).then(tr => setFilteredTypes(tr.data.types || []));
      }
      if (asset?.site_id) {
        locationsAPI.siteLocations(asset.site_id).then(lr => setLocations(lr.data.locations || []));
      }
    });
  }, [asset]);

  const onNatureChange = (id) => {
    setForm(f => ({ ...f, nature_id: id, category_id: '', type_id: '' }));
    if (id) taxonomyAPI.categories({ nature_id: id }).then(r => setFilteredCategories(r.data.categories || []));
    else setFilteredCategories([]);
  };

  const onCategoryChange = (id) => {
    setForm(f => ({ ...f, category_id: id, type_id: '' }));
    if (id) taxonomyAPI.types({ category_id: id }).then(r => setFilteredTypes(r.data.types || []));
    else setFilteredTypes([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] }, maxFiles: 1,
    onDrop: ([f]) => setImage(f)
  });

  const save = async () => {
    if (!form.name || !form.asset_code) return toast.error('Name and code required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      if (image) fd.append('image', image);
      if (asset) await assetsAPI.update(asset.id, fd);
      else await assetsAPI.create(fd);
      toast.success(asset ? 'Asset updated!' : 'Asset created!');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save asset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{asset ? 'Edit Asset' : 'Create Asset'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
              {asset ? `Update details for ${asset.asset_code}` : 'Register new asset and generate QR code'}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '72vh', overflowY: 'auto' }}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Asset Name *</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Chiller Unit Alpha" />
            </div>
            <div className="form-group">
              <label className="form-label">Asset Code *</label>
              <input className="form-input" value={form.asset_code} onChange={e => setForm(f => ({ ...f, asset_code: e.target.value }))} placeholder="e.g. AST-001" style={{ fontFamily: 'var(--font-mono)' }} />
            </div>
          </div>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">Classification Nature</label>
              <select className="form-select" value={form.nature_id} onChange={e => onNatureChange(e.target.value)}>
                <option value="">Select nature</option>
                {natures.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category_id} onChange={e => onCategoryChange(e.target.value)} disabled={!form.nature_id}>
                <option value="">Select category</option>
                {filteredCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Asset Type</label>
              <select className="form-select" value={form.type_id} onChange={e => setForm(f => ({ ...f, type_id: e.target.value }))} disabled={!form.category_id}>
                <option value="">Select type</option>
                {filteredTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description / Purpose</label>
            <textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Operating specifications, technical notes..." rows={2} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Site / Facility</label>
              <select className="form-select" value={form.site_id} onChange={e => {
                const sid = e.target.value;
                setForm({ ...form, site_id: sid, location_id: '' });
                if (sid) locationsAPI.siteLocations(sid).then(lr => setLocations(lr.data.locations || []));
                else setLocations([]);
              }}>
                <option value="">Select Site...</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Spot Location</label>
              <select className="form-select" value={form.location_id} onChange={e => setForm({ ...form, location_id: e.target.value })} disabled={!form.site_id}>
                <option value="">Select Spot...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name} {l.building ? `(${l.building})` : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Custom / Legacy Location</label>
              <input className="form-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Specific room, wing, area, etc." />
            </div>
            <div className="form-group">
              <label className="form-label">Manufacturer</label>
              <input className="form-input" value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} placeholder="e.g. Carrier, Trane, Siemens" />
            </div>
          </div>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label className="form-label">Model</label>
              <input className="form-input" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Serial Number</label>
              <input className="form-input" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} style={{ fontFamily: 'var(--font-mono)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Installation Date</label>
              <input className="form-input" type="date" value={form.installation_date} onChange={e => setForm(f => ({ ...f, installation_date: e.target.value }))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Asset Condition</label>
              <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Criticality</label>
              <select className="form-select" value={form.criticality} onChange={e => setForm(f => ({ ...f, criticality: e.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Asset Photo</label>
            <div {...getRootProps()} className={`drop-zone ${isDragActive ? 'active' : ''}`} style={{ padding: 20 }}>
              <input {...getInputProps()} />
              <div className="drop-zone-icon"><Package size={24} /></div>
              <div className="drop-zone-title">{image ? image.name : (asset?.image_url ? 'Replace current photo' : 'Drop image or click to browse')}</div>
              <div className="drop-zone-sub">JPG, PNG, WebP · Max 10MB</div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : null}
            {asset ? 'Save Changes' : 'Create Asset & Generate QR'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetHistoryModal({ asset, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    assetsAPI.getHistory(asset.id).then(r => setHistory(r.data.history || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [asset.id]);

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setAdding(true);
    try {
      await assetsAPI.addHistory(asset.id, { description: note });
      toast.success('Note added to history');
      setNote('');
      load();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Asset History</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{asset.name} ({asset.asset_code})</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a manual history note or maintenance entry..." />
            <button className="btn btn-primary" onClick={handleAddNote} disabled={adding || !note.trim()}>Add Note</button>
          </div>

          {loading ? <div className="spinner" style={{ margin: '20px auto' }} /> : history.length === 0 ? (
            <div className="empty-state">No history recorded for this asset yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {history.map(h => (
                <div key={h.id} style={{ display: 'flex', gap: 12, padding: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {h.event_type === 'manual_note' ? <MessageSquare size={14} color="var(--accent)" /> : <Clock size={14} color="var(--text-secondary)" />}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--text)' }}>{h.description}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {new Date(h.created_at).toLocaleString()} · {h.created_by_name || 'System'}
                      <span style={{ marginLeft: 8, background: 'var(--bg-muted)', padding: '2px 6px', borderRadius: 4, fontSize: 10, textTransform: 'capitalize' }}>
                        {h.event_type.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SOPLinkModal({ asset, onClose }) {
  const [sops, setSops] = useState([]);
  const [linkedSops, setLinkedSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSop, setSelectedSop] = useState('');
  const [linkType, setLinkType] = useState('specific'); // 'specific' or 'type'

  useEffect(() => {
    Promise.all([
      sopsAPI.list(),
      sopsAPI.getForAsset(asset.id)
    ]).then(([sl, al]) => {
      setSops(sl.data.sops || []);
      setLinkedSops(al.data.sops || []);
    }).finally(() => setLoading(false));
  }, [asset.id]);

  const handleLink = async () => {
    if (!selectedSop) return;
    try {
      await sopsAPI.link({
        sop_id: selectedSop,
        asset_id: linkType === 'specific' ? asset.id : null,
        asset_type_id: linkType === 'type' ? asset.type_id : null
      });
      toast.success('SOP linked successfully');
      setSelectedSop('');
      const al = await sopsAPI.getForAsset(asset.id);
      setLinkedSops(al.data.sops || []);
    } catch {
      toast.error('Failed to link SOP');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Link Procedures (SOPs)</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{asset.name}</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Link Strategy</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className={`btn ${linkType === 'specific' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setLinkType('specific')} style={{ flex: 1 }}>
                This Asset Only
              </button>
              <button className={`btn ${linkType === 'type' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setLinkType('type')} style={{ flex: 1 }} disabled={!asset.type_id}>
                All {asset.type_name || 'Assets of this Type'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Select Procedure</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="form-select" value={selectedSop} onChange={e => setSelectedSop(e.target.value)}>
                <option value="">Select an SOP...</option>
                {sops.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
              <button className="btn btn-primary" onClick={handleLink} disabled={!selectedSop}>Link</button>
            </div>
          </div>

          <div>
            <label className="form-label">Currently Linked</label>
            {loading ? <div className="spinner" style={{ margin: '10px auto' }} /> : linkedSops.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20, border: '1px dashed var(--border)', borderRadius: 8 }}>
                No procedures linked to this asset.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {linkedSops.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    background: 'var(--bg-muted)', borderRadius: 6, border: '1px solid var(--border)'
                  }}>
                    <BookOpen size={14} color="var(--accent)" />
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{s.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AssetDetailModal ────────────────────────────────────────────────────────
function AssetDetailModal({ asset: initialAsset, onClose, onEdit, onClone, onQR, onHistory, onSOP, onDelete, canEdit, canDelete }) {
  const navigate = useNavigate();
  const [assetData, setAssetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // 'overview' | 'jobs' | 'sops' | 'history'

  const fetchFullAsset = useCallback(() => {
    if (!initialAsset?.id) return;
    setLoading(true);
    assetsAPI.get(initialAsset.id)
      .then(res => setAssetData(res.data))
      .catch(() => toast.error('Failed to load asset details'))
      .finally(() => setLoading(false));
  }, [initialAsset?.id]);

  useEffect(() => {
    fetchFullAsset();
  }, [fetchFullAsset]);

  const asset = assetData?.asset || initialAsset;
  const recentJobs = assetData?.recent_jobs || [];
  const linkedSops = assetData?.linked_sops || [];
  const history = assetData?.history || [];
  const bgColor = asset.category_color || avatarColors[asset.name?.charCodeAt(0) % avatarColors.length] || '#0EA5E9';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 840 }}>
        {/* Header */}
        <div className="modal-header" style={{ alignItems: 'flex-start', paddingBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 'var(--r-md)',
              background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${bgColor}40`
            }}>
              <Package size={24} color="#fff" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="modal-title" style={{ fontSize: 20 }}>{asset.name}</span>
                <span className={`badge badge-${asset.status}`} style={{ textTransform: 'capitalize' }}>
                  {asset.status?.replace('_', ' ')}
                </span>
                <span className="badge" style={{
                  fontSize: 11, textTransform: 'capitalize',
                  background: asset.criticality === 'critical' ? '#FEE2E2' : asset.criticality === 'high' ? '#FFEDD5' : '#FEF3C7',
                  color: asset.criticality === 'critical' ? '#991B1B' : asset.criticality === 'high' ? '#C2410C' : '#92400E',
                  fontWeight: 700
                }}>
                  {asset.criticality || 'medium'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>
                  {asset.asset_code}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>·</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {asset.nature_name || 'Asset'}{asset.category_name ? ` › ${asset.category_name}` : ''}{asset.type_name ? ` › ${asset.type_name}` : ''}
                </span>
              </div>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Tab switch */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', padding: '0 24px', background: 'var(--bg-muted)30' }}>
          {[
            { id: 'overview', label: 'Overview & Specs', icon: Package },
            { id: 'jobs', label: `Jobs (${recentJobs.length})`, icon: Layers },
            { id: 'sops', label: `Procedures (${linkedSops.length})`, icon: BookOpen },
            { id: 'history', label: `History (${history.length})`, icon: HistoryIcon }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '12px 14px', border: 'none', background: 'transparent',
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                color: tab === t.id ? 'var(--accent)' : 'var(--text-muted)',
                fontWeight: tab === t.id ? 700 : 500, fontSize: 13, cursor: 'pointer',
                transition: 'all 150ms'
              }}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="modal-body" style={{ padding: 24, maxHeight: '65vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : tab === 'overview' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Top row: Photo / Info preview */}
              <div style={{ display: 'grid', gridTemplateColumns: asset.image_url ? '200px 1fr' : '1fr', gap: 16 }}>
                {asset.image_url && (
                  <div style={{
                    borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--border)',
                    background: 'var(--bg-muted)', maxHeight: 180
                  }}>
                    <img src={getMediaURL(asset.image_url)} alt={asset.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  <div style={{ background: 'var(--bg-muted)50', padding: 12, borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Site & Facility</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>
                      {asset.site_name || 'None Assigned'} {asset.site_code ? `(${asset.site_code})` : ''}
                    </div>
                  </div>
                  <div style={{ background: 'var(--bg-muted)50', padding: 12, borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Spot Location</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>
                      {asset.spot_location_name || '—'} {asset.building ? `· ${asset.building}` : ''}
                    </div>
                  </div>
                  <div style={{ background: 'var(--bg-muted)50', padding: 12, borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Work Orders</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>
                      {asset.total_jobs || 0} Total · <span style={{ color: 'var(--accent)' }}>{asset.active_jobs || 0} Active</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Specifications Grid */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  Technical Specifications
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  {[
                    { label: 'Manufacturer', val: asset.manufacturer || '—' },
                    { label: 'Model', val: asset.model || '—' },
                    { label: 'Serial Number', val: asset.serial_number || '—', mono: true },
                    { label: 'Installation Date', val: asset.installation_date ? new Date(asset.installation_date).toLocaleDateString() : '—' },
                    { label: 'Nature', val: asset.nature_name || '—' },
                    { label: 'Category', val: asset.category_name || '—' },
                    { label: 'Asset Type', val: asset.type_name || '—' },
                    { label: 'Custom Location', val: asset.location || '—' }
                  ].map((spec, idx) => (
                    <div key={idx} style={{ background: 'var(--bg-card)', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>{spec.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 2, fontFamily: spec.mono ? 'var(--font-mono)' : 'inherit' }} className="truncate">
                        {spec.val}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              {asset.description && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    Operational Description
                  </div>
                  <div style={{ padding: '12px 14px', background: 'var(--bg-muted)30', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.5, color: 'var(--text)' }}>
                    {asset.description}
                  </div>
                </div>
              )}

              {/* Quick QR snippet */}
              <div style={{
                padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 'var(--r-md)',
                border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <QrCode size={24} color="var(--accent)" />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Telemetry & Mobile Scanner QR</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{asset.qr_code}</div>
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => onQR(asset)}>
                  <QrCode size={14} /> Open Full QR
                </button>
              </div>
            </div>
          ) : tab === 'jobs' ? (
            <div>
              {recentJobs.length === 0 ? (
                <div className="empty-state" style={{ padding: 40 }}>
                  <Layers size={36} className="empty-icon" />
                  <div className="empty-title">No jobs recorded for this asset</div>
                  <div className="empty-sub">Create a maintenance or inspection work order from the Jobs menu</div>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Job Number</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Due Date</th>
                        <th>Assigned To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentJobs.map(j => (
                        <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => { onClose(); navigate(`/jobs/${j.id}`); }}>
                          <td>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>
                              {j.job_number}
                            </span>
                          </td>
                          <td style={{ fontSize: 13, fontWeight: 600 }}>{j.title}</td>
                          <td><span className={`badge badge-${j.status}`}>{j.status?.replace('_', ' ')}</span></td>
                          <td>
                            <span style={{ fontSize: 12, fontWeight: 700, color: PRIORITY_COLORS[j.priority] || 'var(--text)' }}>
                              {j.priority?.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {j.due_date ? format(parseISO(j.due_date), 'MMM d, HH:mm') : '—'}
                          </td>
                          <td style={{ fontSize: 13 }}>{j.assigned_to_name || 'Unassigned'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : tab === 'sops' ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Standard Operating Procedures available for technicians servicing this asset:
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => onSOP(asset)}>
                  <BookOpen size={14} /> Link Procedure
                </button>
              </div>
              {linkedSops.length === 0 ? (
                <div className="empty-state" style={{ padding: 32 }}>
                  <BookOpen size={36} className="empty-icon" />
                  <div className="empty-title">No SOPs linked yet</div>
                  <div className="empty-sub">Attach SOP procedures to ensure technicians follow standardized checklists</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {linkedSops.map(s => (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border)'
                    }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-muted)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BookOpen size={18} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{s.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          Linked Procedure · Standardized Workflow
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Event log and technician service notes:</div>
                <button className="btn btn-ghost btn-sm" onClick={() => onHistory(asset)}>
                  <Plus size={14} /> Add Manual Note
                </button>
              </div>
              {history.length === 0 ? (
                <div className="empty-state" style={{ padding: 32 }}>No history entries logged.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {history.map(h => (
                    <div key={h.id} style={{ display: 'flex', gap: 12, padding: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Clock size={13} color="var(--text-secondary)" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{h.description}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                          {new Date(h.created_at).toLocaleString()} · {h.created_by_name || 'System'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal footer with full action tools */}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onQR(asset)}>
              <QrCode size={14} /> QR Code
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onHistory(asset)}>
              <HistoryIcon size={14} /> History
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onSOP(asset)}>
              <BookOpen size={14} /> SOPs
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => onClone(asset)}>
                  <Copy size={14} /> Clone
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => onEdit(asset)}>
                  <Edit2 size={14} /> Edit
                </button>
              </>
            )}
            {canDelete && (
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => onDelete(asset)}>
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AssetsPage Main ────────────────────────────────────────────────────────
export default function AssetsPage() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState(() => localStorage.getItem('assets-view') || 'table'); // 'table' | 'grid'
  const [modal, setModal] = useState(null); // null | 'create' | 'edit' | 'detail' | 'qr' | 'import' | 'history' | 'sop'
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Role permissions supporting super_admin, admin, supervisor
  const canCreate = ['super_admin', 'admin', 'supervisor'].includes(user?.role);
  const canEdit = ['super_admin', 'admin', 'supervisor'].includes(user?.role);
  const canDelete = ['super_admin', 'admin'].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      assetsAPI.list({ limit: 500, search, status: statusFilter }),
      assetsAPI.categories()
    ]).then(([ar, cr]) => {
      setAssets(ar.data.assets || []);
      setCategories(cr.data.categories || []);
    }).catch(() => {
      toast.error('Failed to load assets');
    }).finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (asset, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete asset "${asset.name}" (${asset.asset_code})? This cannot be undone.`)) return;
    try {
      await assetsAPI.delete(asset.id);
      toast.success('Asset deleted');
      if (modal === 'detail') setModal(null);
      load();
    } catch {
      toast.error('Failed to delete asset');
    }
  };

  const handleClone = async (asset, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Duplicate asset "${asset.name}"? A new unique code and QR code will be generated.`)) return;
    const tid = toast.loading('Cloning asset...');
    try {
      const res = await assetsAPI.clone(asset.id);
      toast.success(`Asset cloned as ${res.data.asset.asset_code}!`, { id: tid });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clone asset', { id: tid });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    const tid = toast.loading('Exporting assets to Excel...');
    try {
      const res = await assetsAPI.export({ search, status: statusFilter });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      toast.success('Assets exported successfully', { id: tid });
    } catch {
      toast.error('Failed to export assets', { id: tid });
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (asset, e) => {
    if (e) e.stopPropagation();
    setSelected(asset);
    setModal('detail');
  };

  const handleEdit = (asset, e) => {
    if (e) e.stopPropagation();
    setSelected(asset);
    setModal('edit');
  };

  const handleQR = (asset, e) => {
    if (e) e.stopPropagation();
    setSelected(asset);
    setModal('qr');
  };

  const handleHistory = (asset, e) => {
    if (e) e.stopPropagation();
    setSelected(asset);
    setModal('history');
  };

  const handleSOP = (asset, e) => {
    if (e) e.stopPropagation();
    setSelected(asset);
    setModal('sop');
  };

  const statusCounts = useMemo(() => {
    return assets.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});
  }, [assets]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 220 }}>
          <Search size={15} color="var(--text-muted)" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assets by name, code, manufacturer, model..." />
          {search && (
            <button className="btn-icon" onClick={() => setSearch('')} style={{ width: 24, height: 24, padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* View mode switcher */}
        <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: 4, border: '1px solid var(--border)' }}>
          <button
            className={`btn-icon ${view === 'table' ? 'active' : ''}`}
            style={{
              background: view === 'table' ? 'var(--bg-card)' : 'transparent',
              boxShadow: view === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              padding: '4px 10px', height: 'auto', display: 'flex', alignItems: 'center', gap: 4
            }}
            onClick={() => { setView('table'); localStorage.setItem('assets-view', 'table'); }}
            title="Table View"
          >
            <List size={16} /> <span style={{ fontSize: 12, fontWeight: 600 }}>List</span>
          </button>
          <button
            className={`btn-icon ${view === 'grid' ? 'active' : ''}`}
            style={{
              background: view === 'grid' ? 'var(--bg-card)' : 'transparent',
              boxShadow: view === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              padding: '4px 10px', height: 'auto', display: 'flex', alignItems: 'center', gap: 4
            }}
            onClick={() => { setView('grid'); localStorage.setItem('assets-view', 'grid'); }}
            title="Grid View"
          >
            <LayoutGrid size={16} /> <span style={{ fontSize: 12, fontWeight: 600 }}>Grid</span>
          </button>
        </div>

        <button className="btn-icon" onClick={load} title="Refresh Assets"><RefreshCw size={15} /></button>

        <button className="btn btn-ghost" onClick={handleExport} disabled={exporting || loading} title="Download Excel report of assets">
          <Download size={15} /> {exporting ? 'Exporting...' : 'Export'}
        </button>

        {canCreate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setModal('import')}>
              <FileUp size={15} /> Import
            </button>
            <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('create'); }}>
              <Plus size={15} /> New Asset
            </button>
          </div>
        )}
      </div>

      {/* Filter pills */}
      <div className="filter-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {['', 'new', 'operational', 'under_maintenance', 'faulty', 'decommissioned'].map(s => (
          <button key={s} className={`filter-pill ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}>
            {s ? s.replace('_', ' ') : 'All Conditions'}
            {s && statusCounts[s] ? <span style={{ marginLeft: 4, opacity: 0.7 }}>({statusCounts[s]})</span> : null}
          </button>
        ))}
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', val: assets.length, color: 'var(--accent)' },
          { label: 'New', val: assets.filter(a => a.status === 'new').length, color: '#8B5CF6' },
          { label: 'Operational', val: assets.filter(a => a.status === 'operational').length, color: '#10B981' },
          { label: 'Maintenance', val: assets.filter(a => a.status === 'under_maintenance').length, color: '#F59E0B' },
          { label: 'Faulty', val: assets.filter(a => a.status === 'faulty').length, color: '#EF4444' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
            padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 10
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{s.val}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Main Content: Table or Grid */}
      {view === 'table' ? (
        <div className="card">
          {loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><div className="spinner" style={{ width: 28, height: 28 }} /></div>
          ) : assets.length === 0 ? (
            <div className="empty-state">
              <Package size={40} className="empty-icon" />
              <div className="empty-title">No assets found</div>
              <div className="empty-sub">{search ? 'Try adjusting your search or filters' : 'Create your first asset to get started'}</div>
              {canCreate && (
                <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('create'); }}>
                  <Plus size={14} /> Create Asset
                </button>
              )}
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Classification</th>
                    <th>Location</th>
                    <th>Condition</th>
                    <th>Criticality</th>
                    <th>Jobs</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map(asset => {
                    const bgColor = asset.category_color || avatarColors[asset.name?.charCodeAt(0) % avatarColors.length] || '#0EA5E9';
                    return (
                      <tr key={asset.id} style={{ cursor: 'pointer' }} onClick={() => handleViewDetails(asset)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                              width: 36, height: 36, borderRadius: 'var(--r-md)',
                              background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Package size={18} color="#fff" />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{asset.name}</span>
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{asset.asset_code}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{asset.category_name || 'General Asset'}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {asset.nature_name || 'No Nature'}{asset.type_name ? ` › ${asset.type_name}` : ''}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {asset.site_name ? (
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                                {asset.site_name} {asset.spot_location_name ? `· ${asset.spot_location_name}` : ''}
                              </span>
                            ) : (
                              <span style={{ fontSize: 13, color: 'var(--text)' }}>{asset.location || '—'}</span>
                            )}
                            {asset.location && asset.site_name && (
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{asset.location}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${asset.status}`} style={{ textTransform: 'capitalize' }}>
                            {asset.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{
                            fontSize: 11, textTransform: 'capitalize',
                            background: asset.criticality === 'critical' ? '#FEE2E2' : asset.criticality === 'high' ? '#FFEDD5' : '#FEF3C7',
                            color: asset.criticality === 'critical' ? '#991B1B' : asset.criticality === 'high' ? '#C2410C' : '#92400E',
                            fontWeight: 700
                          }}>
                            {asset.criticality || 'medium'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-muted)', color: 'var(--text)' }} title="Total Jobs">
                              {asset.total_jobs || 0} Total
                            </span>
                            {parseInt(asset.active_jobs) > 0 && (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'var(--accent-muted)', color: 'var(--accent)' }} title="Active Jobs">
                                {asset.active_jobs} Active
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                            <button className="btn-icon" title="View Details" onClick={e => handleViewDetails(asset, e)}><Eye size={14} /></button>
                            <button className="btn-icon" title="QR Code" onClick={e => handleQR(asset, e)}><QrCode size={14} /></button>
                            <button className="btn-icon" title="History" onClick={e => handleHistory(asset, e)}><HistoryIcon size={14} /></button>
                            <button className="btn-icon" title="Link SOPs" onClick={e => handleSOP(asset, e)}><BookOpen size={14} /></button>
                            {canCreate && (
                              <button className="btn-icon" title="Clone Asset" onClick={e => handleClone(asset, e)}><Copy size={14} /></button>
                            )}
                            {canEdit && (
                              <button className="btn-icon" title="Edit Asset" onClick={e => handleEdit(asset, e)}><Edit2 size={14} /></button>
                            )}
                            {canDelete && (
                              <button className="btn-icon" title="Delete Asset" style={{ color: 'var(--danger)' }} onClick={e => handleDelete(asset, e)}><Trash2 size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Grid View */
        loading ? (
          <div className="grid-auto">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="card" style={{ padding: 20 }}>
                <div className="skeleton" style={{ height: 120, borderRadius: 8, marginBottom: 12 }} />
                <div className="skeleton" style={{ height: 16, width: '60%', marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 12, width: '80%' }} />
              </div>
            ))}
          </div>
        ) : assets.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <Package size={40} className="empty-icon" />
              <div className="empty-title">No assets found</div>
              <div className="empty-sub">{search ? 'Try adjusting your search or filters' : 'Create your first asset to get started'}</div>
              {canCreate && (
                <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('create'); }}>
                  <Plus size={14} /> Create Asset
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid-auto animate-in">
            {assets.map((asset, i) => {
              const bgColor = asset.category_color || avatarColors[i % avatarColors.length];
              return (
                <div
                  key={asset.id}
                  className="card"
                  style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', animationDelay: `${i * 40}ms`, cursor: 'pointer' }}
                  onClick={() => handleViewDetails(asset)}
                >
                  {/* Card top */}
                  <div style={{
                    height: 72, background: `linear-gradient(135deg, ${bgColor}22, ${bgColor}10)`,
                    display: 'flex', alignItems: 'center', padding: '0 20px',
                    borderBottom: '1px solid var(--border)', position: 'relative'
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 'var(--r-md)',
                      background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Package size={20} color="#fff" />
                    </div>
                    <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', gap: 4 }}>
                      {asset.criticality && (
                        <span className="badge" style={{
                          fontSize: 10, textTransform: 'capitalize',
                          background: asset.criticality === 'critical' ? '#FEE2E2' : asset.criticality === 'high' ? '#FFEDD5' : '#FEF3C7',
                          color: asset.criticality === 'critical' ? '#991B1B' : asset.criticality === 'high' ? '#C2410C' : '#92400E',
                          fontWeight: 700
                        }}>
                          {asset.criticality}
                        </span>
                      )}
                      <span className={`badge badge-${asset.status}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>
                        {asset.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{asset.name}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginTop: 2 }}>
                        {asset.asset_code}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Layers size={11} color="var(--text-muted)" />
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {asset.nature_name || 'Asset'} {asset.category_name ? ` › ${asset.category_name}` : ''} {asset.type_name ? ` › ${asset.type_name}` : ''}
                        </span>
                      </div>
                    </div>
                    {(asset.site_name || asset.location) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <MapPin size={11} color="var(--text-muted)" />
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }} className="truncate">
                          {asset.site_name ? `${asset.site_name}${asset.spot_location_name ? ` · ${asset.spot_location_name}` : ''}` : asset.location}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <div style={{ flex: 1, textAlign: 'center', background: 'var(--bg-muted)', borderRadius: 'var(--r-sm)', padding: '6px 4px' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{asset.total_jobs || 0}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Total Jobs</div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', background: 'var(--accent-muted)', borderRadius: 'var(--r-sm)', padding: '6px 4px' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)' }}>{asset.active_jobs || 0}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Active</div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{
                    padding: '12px 18px', borderTop: '1px solid var(--border)',
                    display: 'flex', gap: 6, alignItems: 'center'
                  }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                      onClick={e => handleViewDetails(asset, e)}>
                      <Eye size={13} /> View
                    </button>
                    <button className="btn-icon" onClick={e => handleQR(asset, e)} title="QR Code">
                      <QrCode size={14} />
                    </button>
                    <button className="btn-icon" onClick={e => handleHistory(asset, e)} title="History">
                      <HistoryIcon size={14} />
                    </button>
                    <button className="btn-icon" onClick={e => handleSOP(asset, e)} title="Link SOP">
                      <BookOpen size={14} />
                    </button>
                    {canCreate && (
                      <button className="btn-icon" onClick={e => handleClone(asset, e)} title="Clone Asset">
                        <Copy size={14} />
                      </button>
                    )}
                    {canEdit && (
                      <button className="btn-icon" onClick={e => handleEdit(asset, e)} title="Edit Asset">
                        <Edit2 size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button className="btn-icon" style={{ color: 'var(--danger)' }}
                        onClick={e => handleDelete(asset, e)} title="Delete Asset">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Modals */}
      {(modal === 'create' || modal === 'edit') && (
        <AssetModal
          asset={modal === 'edit' ? selected : null}
          categories={categories}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}

      {modal === 'detail' && selected && (
        <AssetDetailModal
          asset={selected}
          onClose={() => setModal(null)}
          onEdit={(a) => { setSelected(a); setModal('edit'); }}
          onClone={(a) => handleClone(a)}
          onQR={(a) => { setSelected(a); setModal('qr'); }}
          onHistory={(a) => { setSelected(a); setModal('history'); }}
          onSOP={(a) => { setSelected(a); setModal('sop'); }}
          onDelete={(a) => handleDelete(a)}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      {modal === 'qr' && selected && (
        <QRModal asset={selected} onClose={() => setModal(null)} onRegenerate={load} />
      )}

      {modal === 'history' && selected && (
        <AssetHistoryModal asset={selected} onClose={() => setModal(null)} />
      )}

      {modal === 'import' && (
        <AssetImportModal onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />
      )}

      {modal === 'sop' && selected && (
        <SOPLinkModal asset={selected} onClose={() => setModal(null)} />
      )}
    </div>
  );
}
